import { createContext, useContext, useState, ReactNode } from "react";
import { Reservation } from "@/types/table";

interface DragState {
  isDragging: boolean;
  draggedReservation: Reservation | null;
  hoveredTableId: string | null;
}

interface FloorPlanDragContextType {
  dragState: DragState;
  startDrag: (reservation: Reservation) => void;
  endDrag: () => void;
  setHoveredTable: (tableId: string | null) => void;
}

const FloorPlanDragContext = createContext<FloorPlanDragContextType | null>(null);

export const useFloorPlanDrag = () => {
  const context = useContext(FloorPlanDragContext);
  if (!context) {
    throw new Error("useFloorPlanDrag must be used within a FloorPlanDragProvider");
  }
  return context;
};

export const FloorPlanDragProvider = ({ children }: { children: ReactNode }) => {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedReservation: null,
    hoveredTableId: null,
  });

  const startDrag = (reservation: Reservation) => {
    setDragState({
      isDragging: true,
      draggedReservation: reservation,
      hoveredTableId: null,
    });
  };

  const endDrag = () => {
    setDragState({
      isDragging: false,
      draggedReservation: null,
      hoveredTableId: null,
    });
  };

  const setHoveredTable = (tableId: string | null) => {
    setDragState(prev => ({
      ...prev,
      hoveredTableId: tableId,
    }));
  };

  return (
    <FloorPlanDragContext.Provider value={{ dragState, startDrag, endDrag, setHoveredTable }}>
      {children}
    </FloorPlanDragContext.Provider>
  );
};
